import { DateTime } from 'luxon';
import axios from 'smg/axios';
import { Issuer } from 'smg/cc';
import * as models from 'smg/customer-api/models';
import * as session from 'smg/customer-api/session';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { AccountReady } from '../account-session';
import { BindClearSearch, PageLoader } from '../account';
import { OrganizationView, RenderOrganizationSwitch, ResolveOrganizationView } from '../organization-view';

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const $empty = document.querySelector('#payment-history-empty') as HTMLElement;
    const $emptyTitle = document.querySelector('#payment-history-empty-title') as HTMLElement;
    const $emptyBody = document.querySelector('#payment-history-empty-body') as HTMLElement;
    const $noMatch = document.querySelector('#payment-history-no-match') as HTMLElement;
    const $noMatchTitle = document.querySelector('#payment-history-no-match-title') as HTMLElement;
    const $noMatchBody = document.querySelector('#payment-history-no-match-body') as HTMLElement;

    BindClearSearch();

    let view: OrganizationView;

    const pl: PageLoader<models.OrderPayment> = new PageLoader<models.OrderPayment>({
        cards: { empty: $empty, noMatch: $noMatch },
        init: async () => {
            const user = await session.Session().then(s => s.user);
            view = await ResolveOrganizationView(user);
            RenderOrganizationSwitch(view, user);
        },
        get: () => axios.get('/order-payments', {
            params: {
                'sort': pl.sort(),
                'max': pl.currentPageSize(),
                'data': 'total, created,paymentOption, orderPayments',
                'p': pl.currentPage(),
                'q': pl.search(),
                ...view.params(),
            },
        }).then(resp => {
            const data = models.OrderPaymentsHyejooResponse.fromJSON(resp.data);
            return { rows: data.orderPayments, nextPage: data.nextPage };
        }),
        draw: order => {
            // Grab the template and clone it
            const card = template.Clone('#paymentHistoryCard').firstElementChild as HTMLElement;

            // Place Logo, Issuer, Last4, and Expiration Date
            const logo = card.querySelector('.logo') as HTMLImageElement;
            const issuer = card.querySelector('.issuer') as HTMLElement;
            const creditCardLast4 = card.querySelector('.creditCardLast4') as HTMLElement;
            const exipirationDate = card.querySelector('.creditCardExpirationDate') as HTMLElement;

            // First, check if the paymentoptionID is a credit card
            if ((order.paymentOption?.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) && order.paymentOption.creditCardFirst6) {
                // Do switch cases for issuer and card logo
                const cardCompany = Issuer(String(order.paymentOption.creditCardFirst6));
                switch (cardCompany) {
                case 'JCB':
                    logo.src += 'jcb.svg';
                    break;
                case 'Amex':
                    logo.src += 'amex.svg';
                    break;
                case 'Diners Club':
                    logo.src = String(logo.src);
                    break;
                case 'Visa':
                    logo.src += 'visa.svg';
                    break;
                case 'Mastercard':
                    logo.src += 'mastercard.svg';
                    break;
                case 'Maestro':
                    logo.src += 'maestro.svg';
                    break;
                case 'Discover':
                    logo.src += 'discover.svg';
                    break;
                }

                creditCardLast4.innerText = `ending in ${String(order.paymentOption.creditCardLast4)}`;
                exipirationDate.innerText = `Expires ${order.paymentOption.creditCardExpirationDate}`;
            } else {
                issuer.innerText = order.paymentOption?.paymentOptionType?.name || '';
                logo?.remove();
            }

            if (view.active) {
                const $attribution = card.querySelector('.organization-attribution') as HTMLElement;
                view.fillAttribution($attribution, order.paymentOption?.userID ?? '');
                $attribution.classList.remove('d-none');
            }

            // Place invoice number. Some orders have split payments. So, for every order, we're going to place that many invoice numbers onto the page.
            if (order.orderPayments.length > 0) {
                for (const invoices of order.orderPayments) {
                    const invoiceNumberContainer = template.Clone('#invoiceContainer').firstElementChild as HTMLElement;

                    const invoiceNumber = invoiceNumberContainer.querySelector('#invoiceNumber') as HTMLElement;
                    invoiceNumber.textContent = invoices.order?.invoiceNumber?.toString() || '';

                    const invoiceLink = invoiceNumberContainer.querySelector('#invoiceLink') as HTMLAnchorElement;
                    invoiceLink.href = view.withLens(`${invoiceLink.getAttribute('href') ?? ''}${invoices.order?.id ?? ''}`);

                    const invoiceBox = card.querySelector<HTMLElement>('#invoiceBox');
                    invoiceBox?.appendChild(invoiceNumberContainer);
                }
            }

            // Place Total
            const total = card.querySelector('.total') as HTMLElement;
            total.textContent = DollarFormat(order.total);

            // Place Date
            const orderDate = card.querySelector('.created') as HTMLElement;
            orderDate.textContent = order.created.isValid ? order.created.toLocaleString(DateTime.DATE_SHORT) : '';

            return card;
        },
        afterLoad: state => {
            // A failed init settles before `view` exists, so read it only
            // in the states a resolved view reaches.
            if (state === 'empty' && view.active) {
                const orgName = view.organization?.name ?? '';
                $emptyTitle.textContent = `No payments from ${orgName} yet`;
                $emptyBody.textContent = 'This list fills in as members pay invoices.';
                return;
            }

            if (state === 'no-match') {
                const orgName = view.organization?.name ?? '';
                const search = pl.search().trim();
                if (view.active) {
                    $noMatchTitle.textContent = `Nothing in ${orgName} matches "${search}"`;
                    $noMatchBody.textContent = `Clear the search to see every payment in ${orgName}.`;
                } else {
                    $noMatchTitle.textContent = `No payments match "${search}".`;
                }
            }
        },
    });

    pl.load();
}
